import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import { FormattedMessage } from '../../../../../util/reactIntl';

import { ExternalLink, NamedLink } from '../../../../../components';
import { clearScrapedData } from '../../../../EditListingPage/EditListingPage.duck';

import css from './PriorityLinks.module.css';

/**
 * Create component that shows only a single "Post a new listing" link.
 *
 * @param {*} props contains customLinksMenuClass
 * @returns div with only one link inside.
 */
export const CreateListingMenuLink = props => {
  const { isProvider } = props;
  // const [open, setOpen] = useState(false); // COMMENTED OUT - Dropdown state no longer needed
  const dispatch = useDispatch();

  if (!isProvider) return null;

  // Handler to clear scraped data before navigating to GoPilot
  const handleGoPilotClick = () => {
    dispatch(clearScrapedData());
    console.log('🧹 Navigating to GoPilot - clearing scraped data');
  };

  /* COMMENTED OUT - Old dropdown implementation (keep for potential future restoration)

  const handleManualClick = () => {
    dispatch(clearScrapedData());
    console.log('🧹 Manual listing selected - clearing scraped data');
  };

  const wrapperStyle = { position: 'relative', display: 'inline-block' };
  const menuStyle = {
    position: 'absolute',
    top: '100%',
    right: 0,
    background: '#303b4b',
    border: 'none',
    boxShadow: 'none',
    borderRadius: 6,
    minWidth: 180,
    zIndex: 10,
  };
  const itemStyle = { display: 'block', color: '#fff', textDecoration: 'none' };

  return (
    <div className={props.customLinksMenuClass} style={wrapperStyle}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className={classNames(css.priorityLink, css.highlight)}
        onClick={() => setOpen(o => !o)}
        style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
      >
        <span className={css.priorityLinkLabel}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </button>

      {open ? (
        <div style={menuStyle}>
          <div onClick={handleManualClick}>
            <NamedLink name="NewListingPage" className={css.priorityLink} style={itemStyle}>
              <span className={css.priorityLinkLabel} style={{ color: '#fff' }}>
                <FormattedMessage id="TopbarDesktop.createListing.manual" />
                </span>
            </NamedLink>
          </div>
          <NamedLink name="GopilotPage" className={css.priorityLink} style={itemStyle}>
            <span className={css.priorityLinkLabel} style={{ color: '#fff' }}><FormattedMessage id="TopbarDesktop.createListing.gopilot" /></span>
          </NamedLink>
        </div>
      ) : null}
    </div>
  );
  */

  // NEW: Direct GoPilot link implementation
  return (
    <div className={props.customLinksMenuClass} onClick={handleGoPilotClick}>
      <NamedLink name="GopilotPage">
        <button
          type="button"
          className={classNames(css.priorityLink, css.highlight)}
          style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
        >
          <span className={css.priorityLinkLabel}>
            <FormattedMessage id="TopbarDesktop.createListing" />
          </span>
        </button>
      </NamedLink>
    </div>
  );
};

/**
 * Link component that can be used on TopbarDesktop.
 *
 * @param {*} props containing linkConfig including resolved 'route' params for NamedLink.
 * @returns NamedLink or ExternalLink component based on config.
 */
const PriorityLink = ({ linkConfig }) => {
  const { text, type, href, route, highlight } = linkConfig;
  const classes = classNames(css.priorityLink, { [css.highlight]: highlight });

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    return (
      <NamedLink name={name} params={params} to={to} className={classes}>
        <span className={css.priorityLinkLabel}>{text}</span>
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={classes}>
      <span className={css.priorityLinkLabel}>{text}</span>
    </ExternalLink>
  );
};

/**
 * Create priority links, which are visible on the desktop layout on the Topbar.
 * If space is limited, this doesn't include anything to the Topbar.
 *
 * @param {*} props contains links array and setLinks function
 * @returns container div with priority links included.
 */
const PriorityLinks = props => {
  const containerRef = useRef(null);

  // With this useEffect, we measure the widths of each rendered priority link
  // This is done once before the real rendering and it's done outside the viewport.
  useEffect(() => {
    const isMeasured = props.links?.[0]?.width;
    if (containerRef.current && !isMeasured) {
      const linksFromRenderedWrapper = [...containerRef.current.childNodes];
      let cumulatedWidth = 0;
      // Generate an array of link configs with width & cumulatedWidth included
      const linksWithWidths = props.links.reduce((links, l, i) => {
        const width = linksFromRenderedWrapper[i].offsetWidth;
        cumulatedWidth = cumulatedWidth + width;
        return [...links, { ...l, width, cumulatedWidth }];
      }, []);
      props.setLinks(linksWithWidths);
    }
  }, [containerRef]);

  const { links, priorityLinks } = props;
  const isServer = typeof window === 'undefined';
  const isMeasured = links?.[0]?.width && (priorityLinks.length === 0 || priorityLinks?.[0]?.width);
  const styleWrapper = !!isMeasured
    ? {}
    : {
        style: {
          position: 'absolute',
          top: '-2000px',
          left: '-2000px',
          width: '100%',
          height: 'var(--topbarHeightDesktop)',
          display: 'flex',
          flexDirection: 'row',
        },
      };
  const linkConfigs = isMeasured ? priorityLinks : links;

  return isMeasured || isServer ? (
    <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
      {linkConfigs.map((linkConfig, index) => {
        return <PriorityLink key={`${linkConfig.text}_${index}`} linkConfig={linkConfig} />;
      })}
    </div>
  ) : (
    ReactDOM.createPortal(
      <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
        {linkConfigs.map((linkConfig, index) => {
          return <PriorityLink key={`${linkConfig.text}_${index}`} linkConfig={linkConfig} />;
        })}
      </div>,
      document.body
    )
  );
};

export default PriorityLinks;
